import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Spinner from '../spinner/spinner';
import './AttachmentPreview.scss';
import { getCredentials } from '../../selectors/application';
import { downloadAttachmentFile, convertDocToHtml } from '../../services/message';
import { connect } from 'react-redux';
import DownloadIcon from '../../assets/icons/icon-download.svg?react';
import PrintIcon from '../../assets/icons/icon-print.svg?react';
import CloseIcon from '../../assets/icons/icon-close-big.svg?react';
import AddSquareIcon from '../../assets/icons/icon-add-square.svg?react';
import MinusSquareIcon from '../../assets/icons/icon-minus-square.svg?react';

// Margins and fluid images for the HTML preview iframe (PEC-8727).
const HTML_PREVIEW_STYLE = '<style>body{margin:0;padding:40px clamp(16px,8vw,96px)}img{max-width:100%;height:auto}</style>';

const AttachmentPreview = ({ attachment, onClose, onDownload, credentials }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [htmlContent, setHtmlContent] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [originalFile, setOriginalFile] = useState(null);
  const scrollContainerRef = useRef(null);
  const pageRefs = useRef([]);

  // Helper function to check if file is an image
  const isImageFile = fileName => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const fileExtension = fileName?.toLowerCase().split('.').pop();
    return imageExtensions.includes(fileExtension);
  };

  // Convert file to base64
  const fileToBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const base64ToFile = file => {
    const byteString = atob(file.content);
    const byteNumbers = new Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], file.fileName, { type: file.contentType });
  };

  // Helper function to check if file can be printed directly
  const canPrintDirectly = fileName => {
    const directPrintExtensions = ['pdf'];
    const fileExtension = fileName?.toLowerCase().split('.').pop();
    return directPrintExtensions.includes(fileExtension);
  };

  // Helper function to check if file is a document
  const isDocumentFile = fileName => {
    const documentExtensions = ['doc', 'docx', 'rtf', 'odt', 'txt', 'csv'];
    const fileExtension = fileName?.toLowerCase().split('.').pop();
    return documentExtensions.includes(fileExtension);
  };

  const handleDownloadAndConvert = async () => {
    try {
      setIsLoading(true);

      // Step 1: Download the file
      const file = attachment.content ?
        base64ToFile(attachment) :
        await downloadAttachmentFile(credentials, attachment);
      setOriginalFile(file);

      // Step 2: Check if it's an image file
      if (isImageFile(attachment?.fileName)) {
        // Convert image directly to base64
        const base64Image = await fileToBase64(file);
        setImages([base64Image]);
        setHtmlContent('');
      } else {
        // Step 3: Send non-image files to the conversion service
        const conversionResult = await convertDocToHtml(file);
        setImages(conversionResult?.images || []);
        setHtmlContent(conversionResult?.html || '');
      }
    } catch (error) {
      console.error('Error downloading or converting the file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;

    let currentPageIndex = 0;
    let maxVisibleArea = 0;

    pageRefs.current.forEach((pageRef, index) => {
      if (!pageRef) {
        return;
      }

      const rect = pageRef.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const visibleTop = Math.max(rect.top, containerRect.top);
      const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight > maxVisibleArea) {
        maxVisibleArea = visibleHeight;
        currentPageIndex = index;
      }
    });

    if (currentPageIndex !== currentIndex) {
      setCurrentIndex(currentPageIndex);
    }
  };

  const scrollToPage = pageIndex => {
    const pageRef = pageRefs.current[pageIndex];
    if (pageRef && scrollContainerRef.current) {
      pageRef.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Zoom functions
  const zoomIn = () => {
    setZoomLevel(prevZoom => Math.min(prevZoom + 0.25, 3)); // Max zoom 300%
  };

  const zoomOut = () => {
    setZoomLevel(prevZoom => Math.max(prevZoom - 0.25, 0.5)); // Min zoom 50%
  };

  const resetZoom = () => {
    setZoomLevel(1); // Reset to 100%
  };

  const nextImage = () => {
    if (currentIndex < images.length - 1) {
      scrollToPage(currentIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentIndex > 0) {
      scrollToPage(currentIndex - 1);
    }
  };

  // Enhanced handleKeyDown to include zoom controls
  const handleKeyDown = e => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowRight':
        nextImage();
        break;
      case 'ArrowLeft':
        prevImage();
        break;
      case '+':
      case '=':
        e.preventDefault();
        zoomIn();
        break;
      case '-':
        e.preventDefault();
        zoomOut();
        break;
      case '0':
        e.preventDefault();
        resetZoom();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    handleDownloadAndConvert();
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = 'auto';

      document.removeEventListener('keydown', handleKeyDown);

      // images.forEach(image => {
      //   if (image.startsWith('blob:')) {
      //     URL.revokeObjectURL(image);
      //   }
      // });
    };
  }, []);

  const handlePrint = () => {
    // Handle PDF files - open directly for printing
    if (canPrintDirectly(attachment?.fileName) && originalFile) {
      try {
        const fileURL = URL.createObjectURL(originalFile);
        const printWindow = window.open(fileURL, '_blank');

        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            setTimeout(() => {
              URL.revokeObjectURL(fileURL);
            }, 1000);
          };
        }
        return;
      } catch (error) {
        console.error('Error printing PDF directly:', error);
      }
    }

    // Handle document files - try to open directly first
    if (isDocumentFile(attachment?.fileName) && originalFile) {
      try {
        const fileURL = URL.createObjectURL(originalFile);
        const printWindow = window.open('', '_blank');

        if (printWindow) {
          printWindow.document.write(`
            <html>
              <head>
                <title>${attachment?.fileName}</title>
                <style>
                  body {
                    margin: 0;
                    padding: 20px;
                    font-family: Arial, sans-serif;
                  }
                  .document-container {
                    width: 100%;
                    height: 100vh;
                  }
                  iframe {
                    width: 100%;
                    height: 100%;
                    border: none;
                  }
                  .fallback-message {
                    text-align: center;
                    margin-top: 50px;
                    font-size: 18px;
                  }
                  @media print {
                    body { margin: 0; padding: 0; }
                    .fallback-message { display: none; }
                  }
                </style>
              </head>
              <body>
                <div class="document-container">
                  <iframe src="${fileURL}" onload="window.print()"></iframe>
                  <div class="fallback-message">
                    <p>${t('attachment_preview.document_fallback')}</p>
                    <p><a href="${fileURL}" download="${attachment?.fileName}">${t('attachment_preview.download_file', { fileName: attachment?.fileName })}</a></p>
                  </div>
                </div>
                <script>
                  setTimeout(function() {
                    window.close();
                  }, 2000);
                </script>
              </body>
            </html>
          `);
          printWindow.document.close();

          setTimeout(() => {
            URL.revokeObjectURL(fileURL);
          }, 3000);
        }
        return;
      } catch (error) {
        console.error('Error printing document directly:', error);
      }
    }

    // Fallback: Print using images array (for images or when direct printing fails)
    if (images.length > 0) {
      const printWindow = window.open('', '_blank');

      if (printWindow) {
        const imagesHtml = images.map((image, index) =>
          `<div class="page-break">
             <img src="${image}" alt="${attachment?.fileName} - ${t('attachment_preview.page')} ${index + 1}" />
           </div>`
        ).join('');

        printWindow.document.write(`
          <html>
            <head>
              <title>${attachment?.fileName}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  font-family: Arial, sans-serif;
                }
                .page-break {
                  page-break-after: always;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  margin-bottom: 20px;
                }
                .page-break:last-child {
                  page-break-after: avoid;
                  margin-bottom: 0;
                }
                img {
                  max-width: 100%;
                  max-height: 100vh;
                  object-fit: contain;
                }
                @media print {
                  body {
                    margin: 0;
                    padding: 0;
                  }
                  .page-break {
                    margin-bottom: 0;
                    min-height: 100vh;
                  }
                }
              </style>
            </head>
            <body>
              ${imagesHtml}
              <script>
                window.onload = function() {
                  window.print();
                  window.setTimeout(function() { window.close(); }, 1000);
                }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
      return;
    }

    if (htmlContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = () => {
          printWindow.print();
          window.setTimeout(() => {
            printWindow.close();
          }, 1000);
        };
      }
    }
  };

  const addAttachmentImg = fileName => {
    const fileExtension = fileName?.toLowerCase().split('.').pop();

    let imgPath;

    switch (fileExtension) {
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
      case 'jpg':
      case 'jpeg':
        imgPath = "/assets/images/icon-microsoft-img.svg";
        break;

      case 'pdf':
        imgPath = "/assets/images/icon-microsoft-pdf.svg";
        break;

      case 'doc':
      case 'docx':
        imgPath = "/assets/images/icon-microsoft-word.svg";
        break;

      case 'csv':
      case 'xlsx':
      case 'xls':
        imgPath = "/assets/images/icon-microsoft-excel.svg";
        break;

      case 'ppt':
      case 'pptx':
        imgPath = "/assets/images/icon-microsoft-ppt.svg";
        break;

      // Media formats
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'mkv':
      case 'wmv':
      case 'flv':
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'flac':
        imgPath = "assets/images/icon-media.svg";
        break;

      // Compressed formats
      case 'zip':
        imgPath = "/assets/images/icon-zip.svg";
        break;

      case 'rar':
      case 'tar':
      case 'gz':
      case '7z':
        imgPath = "/assets/images/icon-compressed.svg";
        break;

      case 'html':
      case 'htm':
        imgPath = "/assets/images/icon-html.svg";
        break;

      default:
        imgPath = "/assets/images/icon-doc.svg";
        break;
    }

    return imgPath;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={'loading-container'}>
          <Spinner visible={true} />
        </div>
      );
    }

    if (images.length > 0) {
      return (
        <div
          className={'pdf-book-container'}
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          <div
            className={'pages-wrapper'}
            style={{ transform: `scale(${zoomLevel})` }}
          >
            {images.map((image, index) => (
              <div
                key={index}
                ref={el => {
                  pageRefs.current[index] = el;
                }}
                className={`page ${index === currentIndex ? 'current-page' : ''}`}
                data-page-number={index + 1}
              >
                <div className={'page-content'}>
                  <img
                    src={image}
                    alt={`${attachment?.fileName} - ${t('attachment_preview.page')} ${index + 1}`}
                    className={'page-image'}
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (htmlContent) {
      return (
        <div className={'html-preview-container'}>
          <iframe
            title={attachment?.fileName || 'document-preview'}
            srcDoc={HTML_PREVIEW_STYLE + htmlContent}
            className={'html-preview-iframe'}
          />
        </div>
      );
    }

    return (
      <div className={'error-container'}>
        <p>{t('attachment_preview.no_preview')}</p>
      </div>
    );
  };

  const renderZoomIcon = zoom => {
    if (zoom < 1) {
      return (
        <span className={'zoom-icon'}>
          <i className='lf-icon lf-icon-zoom-in'/>
        </span>
      );
    } else if (zoom > 1) {
      return (
        <span className={'zoom-icon'}>
          <i className='lf-icon lf-icon-zoom-out'/>
        </span>
      );
    }
    return (
      <span className={'zoom-icon'}>
        <i className='lf-icon lf-icon-search'/>
      </span>
    );
  };

  return (
    <div className={'attachment-preview-overlay'}>
      <div className={'attachment-preview-container'}>
        <div className={'attachment-preview-header'}>
          <div className={'attachment-name'}>
            <img
              className={'attachment-image'}
              src={addAttachmentImg(attachment?.fileName)}
              alt={attachment?.fileName}
            />
            <span>{attachment?.fileName}</span>
          </div>
          <div className={'attachment-controls'}>
            <button
              className={'control-button'}
              onClick={onDownload}
              title={t('attachment_preview.download')}
            >
              <DownloadIcon className={'control-icon'} />
              <span>{t('attachment_preview.download')}</span>
            </button>
            <button
              className={'control-button'}
              onClick={handlePrint}
              title={t('attachment_preview.print')}
              disabled={(images.length === 0 && !htmlContent) || isLoading}
            >
              <PrintIcon className={'control-icon'} />
              <span>{t('attachment_preview.print')}</span>
            </button>
            <button
              className={`control-button control-close-button`}
              onClick={onClose}
              title={t('attachment_preview.close')}
            >
              <CloseIcon className={'control-icon'} />
            </button>
          </div>
        </div>

        <div className={'attachment-preview-content'}>
          {renderContent()}
        </div>

        {images.length > 1 && (
          <div className={'attachment-preview-footer'}>
            <div className={'pagination-controls'}>
              <div className={'page-selector'}>
                <span>{t('attachment_preview.page')}</span>
                <input
                  type="number"
                  value={currentIndex + 1}
                  min={1}
                  max={images.length}
                  onChange={e => {
                    const newPage = parseInt(e.target.value, 10);
                    if (newPage >= 1 && newPage <= images.length) {
                      scrollToPage(newPage - 1);
                    }
                  }}
                  className={'page-input'}
                />
                <span className={'page-separator'}>{t('attachment_preview.of')}</span>
                <span className={'page-total'}>{images.length}</span>
              </div>

              {/* Zoom Controls */}
              <div className={'zoom-controls'}>
                <button
                  className={'zoom-button'}
                  onClick={zoomOut}
                  disabled={zoomLevel <= 0.5}
                  title={t('attachment_preview.zoom_out')}
                >
                  <MinusSquareIcon />
                </button>
                <span className={'zoom-indicator'} onClick={resetZoom}>
                  {Math.round(zoomLevel * 100)}%
                  {renderZoomIcon(zoomLevel)}
                </span>
                <button
                  className={'zoom-button'}
                  onClick={zoomIn}
                  disabled={zoomLevel >= 3}
                  title={t('attachment_preview.zoom_in')}
                >
                  <AddSquareIcon />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AttachmentPreview.propTypes = {
  attachment: PropTypes.shape({
    fileName: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    content: PropTypes.any,
    id: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  credentials: PropTypes.object
};

const mapStateToProps = state => ({
  credentials: getCredentials(state)
});

export default connect(mapStateToProps)(AttachmentPreview);
